import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Undo2, X } from 'lucide-react';

/**
 * Deferred deletion with an undo window.
 *
 * The delete is *postponed*, not reversed. The obvious alternative — delete now,
 * re-create on undo — cannot work here: `logged_at` is stamped by Postgres with
 * server_default=now(), so undoing the deletion of a three-week-old weigh-in
 * would file it under today and quietly corrupt the history the forecasts read.
 * Nothing in the API lets a client set that column, and it should not.
 *
 * So the row is hidden immediately, the request is held for UNDO_WINDOW_MS, and
 * undo simply cancels it. The failure direction is deliberate: if the commit
 * never happens — tab closed, network gone — the entry survives. Losing a
 * deletion is recoverable by deleting again; losing the entry is not.
 */

const UNDO_WINDOW_MS = 7000;

const UndoContext = createContext(null);

export function useUndo() {
  const ctx = useContext(UndoContext);
  if (!ctx) throw new Error('useUndo must be used inside <UndoProvider>');
  return ctx;
}

export function UndoProvider({ children }) {
  const [pending, setPending] = useState([]);
  // Timers live in a ref so a re-render never loses one mid-flight.
  const timers = useRef(new Map());
  const nextId = useRef(1);

  const settle = useCallback((id) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setPending((list) => list.filter((entry) => entry.id !== id));
  }, []);

  const commit = useCallback(async (entry) => {
    try {
      await entry.commit();
      if (entry.onCommitted) entry.onCommitted();
    } catch (err) {
      // The server still has the row, but the screen does not. Put it back
      // rather than leaving the user looking at a list that lies.
      entry.restore();
      if (entry.onError) entry.onError(err);
    } finally {
      settle(entry.id);
    }
  }, [settle]);

  /**
   * @param label    what was removed, shown in the toast
   * @param remove   hide it from the UI now (optimistic)
   * @param restore  put it back exactly where it was
   * @param commit   perform the real deletion, called once the window closes
   */
  const undoableDelete = useCallback(({ label, remove, restore, commit: doCommit,
                                       onCommitted, onError }) => {
    const id = nextId.current++;
    remove();
    const entry = { id, label, restore, commit: doCommit, onCommitted, onError };
    setPending((list) => [...list, entry]);
    timers.current.set(id, setTimeout(() => commit(entry), UNDO_WINDOW_MS));
  }, [commit]);

  const undo = useCallback((id) => {
    setPending((list) => {
      const entry = list.find((e) => e.id === id);
      if (entry) entry.restore();
      return list.filter((e) => e.id !== id);
    });
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  // Leaving the page with a deletion still held: commit it rather than dropping
  // it, so the user's intent is not silently discarded. keepalive lets the
  // request outlive the document. If it does not make it, the entry stays —
  // which is the direction we want to fail in.
  useEffect(() => {
    const flush = () => {
      pending.forEach((entry) => {
        try { entry.commit({ keepalive: true }); } catch { /* best effort */ }
      });
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [pending]);

  return (
    <UndoContext.Provider value={{ undoableDelete }}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {pending.map((entry) => (
          <div key={entry.id} className="toast">
            <span className="toast-text">{entry.label} deleted</span>
            <button type="button" className="toast-undo" onClick={() => undo(entry.id)}>
              <Undo2 size={14} aria-hidden="true" /> Undo
            </button>
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss and delete now"
              onClick={() => {
                const timer = timers.current.get(entry.id);
                if (timer) { clearTimeout(timer); timers.current.delete(entry.id); }
                commit(entry);
              }}
            >
              <X size={13} aria-hidden="true" />
            </button>
            <span className="toast-timer" style={{ animationDuration: `${UNDO_WINDOW_MS}ms` }} />
          </div>
        ))}
      </div>
    </UndoContext.Provider>
  );
}
